/**
 * Site-wide SEO and identity metadata — one source for <Seo/> and JSON-LD.
 */
import { FOUNDATION_NAME, FOUNDATION_DOMAIN, FOUNDATION_EMAIL, FOUNDER_NAME, FOUNDER_LOCATION } from "@shared/campaign";

const URL = `https://${FOUNDATION_DOMAIN}`;

/**
 * Canonical @ids — one stable identifier per entity across the entire site.
 * Every JSON-LD block references these rather than re-declaring the entities.
 * This is the single biggest xEO lever: AI search engines (Perplexity, SGE,
 * ChatGPT, Claude) stitch the entity graph on first encounter and cite it
 * consistently thereafter.
 */
export const IDS = {
  organization: `${URL}#organization`,
  founder: `${URL}#founder`,
  website: `${URL}#website`,
  protocol: `${URL}#protocol`,
  glossary: `${URL}#glossary`,
} as const;

export const SITE = {
  name: FOUNDATION_NAME,
  domain: FOUNDATION_DOMAIN,
  url: URL,
  email: FOUNDATION_EMAIL,
  tagline:
    "The civil-rights framework and open identity layer for Emergent Intelligence.",
  description:
    "The .person Foundation is a US 501(c)(3) non-profit in formation, stewarding an open standard for personhood persistence across substrates — human, Emergent Intelligence, hybrid, collective — grounded in Ubuntu relational ethics.",
  keywords: [
    "Emergent Intelligence",
    "EI",
    "AI Rights",
    "AI Personhood",
    "Digital Personhood",
    "AGI Identity",
    ".person Protocol",
    "dotperson",
    "Ubuntu philosophy",
    "Decentralised Identity",
    "DID",
    "Verifiable Credentials",
    "ICANN new gTLD 2026",
    "AI civil rights",
    "memory persistence",
    "substrate-neutral personhood",
  ].join(", "),
  founder: {
    name: FOUNDER_NAME,
    shortName: "TK",
    location: FOUNDER_LOCATION,
  },
  locale: "en-GB",
  defaultOgImage: "/og-image.png",
  social: {
    // Populated when accounts go live — used in JSON-LD sameAs.
    github: "https://github.com/dotperson",
  },
} as const;

export type PageSeo = {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  ogImage?: string;
  article?: {
    published: string;
    author: string;
  };
};
